import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button, Alert } from "@repo/ui";
import copy from "copy-to-clipboard";
import EmailChip from "../../../../components/EmailChip/EmailChip";
import { useAppDispatch, useAppSelector } from "../../../../hooks/useAppDispatch";
import { RootState } from "../../../../redux/store";
import {
  checkGiftTableSlug,
  updateGiftTable as updateGiftTableApi,
} from "../../../../services/giftTableApiService";
import { sendTemplateEmail } from "../../../../services/emailTemplateApiService";
import { generatePermanentURL } from "../../../../services/mediaApiService";
import LoadingButton from "../../../../components/LoadingButton/LoadingButton";
import { useTranslation } from "react-i18next";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";
import { updateGiftTableState } from "../../../../redux/slices/giftTableSlice";

const LOCALE = "modules_MyGiftTable_components_ShareTabContent";

interface StepHeaderProps {
  step: number;
  title: string;
  description?: string;
}

const StepHeader: React.FC<StepHeaderProps> = ({
  step,
  title,
  description,
}) => (
  <div className="flex items-start gap-3">
    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white text-sm font-semibold">
      {step}
    </div>
    <div>
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      {description && (
        <p className="text-sm text-gray-600 mt-0.5">{description}</p>
      )}
    </div>
  </div>
);

type AlertState = {
  message: string;
  level: "info" | "warn" | "error" | "success";
};

const ShareTabContent: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const dispatch = useAppDispatch();
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState<string[]>([]);
  const [shareLink, setShareLink] = useState("");
  const [customSlug, setCustomSlug] = useState("");
  const [originalSlug, setOriginalSlug] = useState("");
  const [lastValidatedValue, setLastValidatedValue] = useState<string>("");
  const [normalizedSlug, setNormalizedSlug] = useState<string | null>(null);
  const [isSlugValid, setIsSlugValid] = useState<boolean | null>(null);
  const [slugAlert, setSlugAlert] = useState<AlertState | null>(null);
  const [copyAlert, setCopyAlert] = useState<AlertState | null>(null);
  const [emailAlert, setEmailAlert] = useState<AlertState | null>(null);
  const [profilePictureURL, setProfilePictureURL] = useState<string | null>(
    null
  );
  const [isSending, setIsSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const qrRef = useRef<SVGSVGElement | null>(null);

  const giftTableID = useAppSelector((state: RootState) => state.giftTable.ID);
  const eventName = useAppSelector(
    (state: RootState) => state.giftTable.eventName
  );
  const amphitryon = useAppSelector(
    (state: RootState) => state.giftTable.amphitryon
  );
  const profilePicture = useAppSelector(
    (state: RootState) => state.giftTable.profilePicture
  );
  const currentSlug = useAppSelector((state: RootState) => state.giftTable.slug);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const shareLinkBase = useMemo(() => {
    const envBase = (import.meta.env.VITE_LANDING_APP_URL || "").replace(
      /\/$/,
      ""
    );
    if (envBase) {
      return envBase;
    }
    if (typeof window !== "undefined") {
      return window.location.origin;
    }
    return "";
  }, []);

  const buildShareLink = useCallback(
    (slugValue: string) =>
      slugValue ? `${shareLinkBase}/gift-table/${slugValue}` : "",
    [shareLinkBase]
  );

  const personalizeTitle = t("steps.personalizeTitle", {
    defaultValue: "Customize your link",
  });
  const personalizeDescription = t("steps.personalizeDescription", {
    defaultValue: "Choose a friendly link so guests can find you easily.",
  });
  const aliasExampleText = t("steps.aliasExample", {
    defaultValue: "Example: boda-andrea-y-jose",
  });
  const saveAliasButtonLabel = t("steps.saveAliasButtonLabel", {
    defaultValue: "Save link",
  });
  const copyTitle = t("steps.copyTitle", {
    defaultValue: "Copy the link",
  });
  const copyDescription = t("steps.copyDescription", {
    defaultValue: "Share it via WhatsApp, social media, email or wherever you like.",
  });
  const copyButtonLabel = t("steps.copyButtonLabel", {
    defaultValue: "Copy link",
  });
  const qrTitle = t("steps.qrTitle", {
    defaultValue: "Download the QR code",
  });
  const qrDescriptionText = t("steps.qrDescriptionDetailed", {
    defaultValue: "Keep it handy to print it or include it in your invitations.",
  });
  const downloadQrButtonLabel = t("steps.downloadQrButtonLabel", {
    defaultValue: "Download QR code",
  });
  const emailStepTitle = t("steps.emailStepTitle", {
    defaultValue: "Add emails and send invitations",
  });
  const emailStepDescription = t("steps.emailStepDescription", {
    defaultValue: "Type an email and click the checkmark to add it to the list.",
  });
  const addEmailHint = t("steps.addEmailHint", {
    defaultValue: "Add as many emails as you need, we'll send them all together.",
  });
  const addEmailButtonAria = t("steps.addEmailButtonAria", {
    defaultValue: "Add email",
  });

  const slugHasChanged =
    customSlug !== originalSlug && customSlug.trim() !== "";
  const canSaveSlug = Boolean(slugHasChanged && customSlug.trim().length > 0);
  const hostsDisplay =
    amphitryon?.trim() ||
    eventName?.trim() ||
    t("defaultHosts", { defaultValue: "Your hosts" });

  useEffect(() => {
    if (!slugAlert) return;
    const timeout = setTimeout(() => setSlugAlert(null), 5000);
    return () => clearTimeout(timeout);
  }, [slugAlert]);

  useEffect(() => {
    if (!copyAlert) return;
    const timeout = setTimeout(() => setCopyAlert(null), 5000);
    return () => clearTimeout(timeout);
  }, [copyAlert]);

  useEffect(() => {
    if (!emailAlert) return;
    const timeout = setTimeout(() => setEmailAlert(null), 5000);
    return () => clearTimeout(timeout);
  }, [emailAlert]);

  useEffect(() => {
    if (!currentSlug) {
      setCustomSlug("");
      setOriginalSlug("");
      setShareLink("");
      setIsSlugValid(null);
      setLastValidatedValue("");
      setNormalizedSlug(null);
      return;
    }
    setCustomSlug(currentSlug);
    setOriginalSlug(currentSlug);
    setShareLink(buildShareLink(currentSlug));
    setIsSlugValid(null);
    setLastValidatedValue(currentSlug);
    setNormalizedSlug(currentSlug);
  }, [currentSlug, buildShareLink]);

  useEffect(() => {
    let active = true;
    const fetchProfilePicture = async () => {
      if (!profilePicture) {
        setProfilePictureURL(null);
        return;
      }
      try {
        const pictureResponse = await generatePermanentURL(profilePicture);
        if (active) setProfilePictureURL(pictureResponse.signedURL);
      } catch (error) {
        console.error("Error generating profile picture URL:", error);
        if (active) setProfilePictureURL(null);
      }
    };

    fetchProfilePicture();
    // Renew the short-lived URL while a share/QR editor remains open.
    const renewal = window.setInterval(fetchProfilePicture, 10 * 60 * 1000);
    return () => { active = false; window.clearInterval(renewal); };
  }, [profilePicture]);

  const validateSlug = async (nextValue: string) => {
    setLastValidatedValue(nextValue);
    if (!nextValue.trim()) {
      setIsSlugValid(false);
      setNormalizedSlug(null);
      return;
    }

    if (nextValue === originalSlug) {
      setIsSlugValid(null);
      setNormalizedSlug(nextValue);
      return;
    }

    try {
      const { available, slug } = await checkGiftTableSlug(
        nextValue,
        giftTableID || undefined
      );
      setIsSlugValid(available);
      setNormalizedSlug(slug);
    } catch (error) {
      if ((error as Error).message === "INVALID_SLUG") {
        setIsSlugValid(false);
      } else {
        console.error("Error validating slug:", error);
        setIsSlugValid(false);
      }
      setNormalizedSlug(null);
    }
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSlug = e.target.value;
    setCustomSlug(newSlug);
    validateSlug(newSlug);
  };

  const ensureSlugAvailability = async (): Promise<string | null> => {
    if (!giftTableID || !slugHasChanged) {
      return null;
    }

    if (lastValidatedValue === customSlug && isSlugValid) {
      return normalizedSlug || customSlug;
    }

    try {
      const { available, slug } = await checkGiftTableSlug(
        customSlug,
        giftTableID
      );
      if (!available) {
        setIsSlugValid(false);
        return null;
      }
      setIsSlugValid(true);
      setLastValidatedValue(customSlug);
      setNormalizedSlug(slug);
      return slug;
    } catch (error) {
      if ((error as Error).message === "INVALID_SLUG") {
        setSlugAlert({
          message: t("slugInvalid", {
            defaultValue: "Use only letters, numbers, and hyphens.",
          }),
          level: "error",
        });
      } else {
        console.error("Error validating slug:", error);
        setSlugAlert({
          message: t("slugValidationError", {
            defaultValue: "We couldn't validate your link. Please try again.",
          }),
          level: "error",
        });
      }
      setIsSlugValid(false);
      return null;
    }
  };

  const handleSlugSave = async () => {
    if (!giftTableID || !slugHasChanged) {
      return;
    }

    const nextSlug = await ensureSlugAvailability();
    if (!nextSlug) {
      return;
    }

    try {
      await updateGiftTableApi({
        id: giftTableID,
        slug: nextSlug,
      });
      dispatch(
        updateGiftTableState({
          slug: nextSlug,
        })
      );
      setOriginalSlug(nextSlug);
      setCustomSlug(nextSlug);
      setShareLink(buildShareLink(nextSlug));
      setSlugAlert({
        message: t("aliasUpdateSuccess"),
        level: "success",
      });
    } catch (error) {
      console.error("Error updating slug:", error);
      setSlugAlert({
        message: t("aliasUpdateError"),
        level: "error",
      });
    }
  };

  const downloadQRCode = () => {
    if (!qrRef.current) return;

    try {
      const svgElement = qrRef.current;
      const serializer = new XMLSerializer();
      const svgData = serializer.serializeToString(svgElement);
      const blob = new Blob([svgData], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${customSlug || "gift-table"}-qr.svg`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading QR code:", error);
    }
  };

  const triggerCopyAction = () => {
    if (!shareLink) {
      setCopyAlert({
        message: t("linkLoadError", {
          defaultValue: "Aún no podemos generar el enlace.",
        }),
        level: "error",
      });
      return;
    }
    copy(shareLink);
    setCopyAlert({ message: t("linkCopied"), level: "success" });
  };

  const handleBlur = () => {
    if (slugHasChanged && isSlugValid) {
      handleSlugSave();
    }
  };

  const addEmail = (): string[] => {
    const trimmedEmail = emailInput.trim();
    if (!trimmedEmail) return emails;

    let updatedEmails = emails;
    if (!emails.includes(trimmedEmail) && emailRegex.test(trimmedEmail)) {
      updatedEmails = [...emails, trimmedEmail];
      setEmails(updatedEmails);
    } else if (!emailRegex.test(trimmedEmail)) {
      setEmailAlert({
        message: t("invalidEmail"),
        level: "error",
      });
    }

    setEmailInput("");
    return updatedEmails;
  };

  const handleAddEmail = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmail();
    }
  };

  const handleAddEmailClick = () => {
    addEmail();
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmails(emails.filter((email) => email !== emailToRemove));
  };

  const handleSendEmail = async () => {
    const emailsToSend = addEmail();
    if (emailsToSend.length === 0) {
      setEmailAlert({
        message: t("noEmailError"),
        level: "info",
      });
      return;
    }

    setIsSending(true);
    setProgress(10);

    try {
      await sendTemplateEmail({
        template_schema: "gift-table-invitation",
        recipients: emailsToSend,
        subject: t("emailSubject", {
          hosts: hostsDisplay,
          defaultValue: "{{hosts}} invite you to their celebration",
        }),
        data: {
          shareLink,
          imageUrl: profilePictureURL || "",
          title: eventName,
          currentYear: new Date().getFullYear().toString(),
        },
      });

      setProgress(80);

      setEmailAlert({
        message: t("emailSentSuccess"),
        level: "success",
      });
      setEmails([]);
    } catch (error) {
      console.error("Error sending email:", error);
      setEmailAlert({
        message: t("emailSendError"),
        level: "error",
      });
    } finally {
      setProgress(100);
      setTimeout(() => {
        setIsSending(false);
        setProgress(0);
      }, 500);
    }
  };

  return (
    <div className="p-4 space-y-10">
      <p className="text-gray-700">{t("shareDescription")}</p>

      <section className="rounded-xl border border-gray-200 p-4 shadow-sm">
        <StepHeader
          step={1}
          title={personalizeTitle}
          description={personalizeDescription}
        />
        <div className="mt-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <input
              type="text"
              value={customSlug}
              onChange={handleSlugChange}
              onBlur={handleBlur}
              onKeyDown={(e) => e.key === "Enter" && handleSlugSave()}
              placeholder={aliasExampleText}
              className={`w-full border p-2 rounded ${
                isSlugValid === false
                  ? "border-red-500 bg-red-50"
                  : isSlugValid
                    ? "border-green-500 bg-green-50"
                    : "border-gray-300"
              }`}
            />
            <Button
              label={saveAliasButtonLabel}
              onClick={handleSlugSave}
              type="secondary"
              disabled={!canSaveSlug}
              className="w-full sm:w-auto"
            />
          </div>
          <p className="text-sm text-gray-500 mt-2">{aliasExampleText}</p>
          {slugAlert && (
            <div className="mt-3">
              <Alert message={slugAlert.message} level={slugAlert.level} />
            </div>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 p-4 shadow-sm">
        <StepHeader
          step={2}
          title={copyTitle}
          description={copyDescription}
        />
        <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center">
          <input
            type="text"
            value={shareLink}
            readOnly
            placeholder={t("linkLoadError")}
            className="w-full border border-gray-300 p-2 rounded bg-gray-50"
          />
          <Button
            label={copyButtonLabel}
            onClick={triggerCopyAction}
            type="primary"
            disabled={!shareLink}
            className="w-full md:w-auto"
          />
        </div>
        {copyAlert && (
          <div className="mt-3">
            <Alert message={copyAlert.message} level={copyAlert.level} />
          </div>
        )}
      </section>

      <section className="rounded-xl border border-gray-200 p-4 shadow-sm">
        <StepHeader
          step={3}
          title={qrTitle}
          description={qrDescriptionText}
        />
        <div className="mt-6 flex flex-col items-center gap-4 sm:flex-row sm:items-start">
          <QRCodeSVG value={shareLink || " "} size={160} ref={qrRef} />
          <Button
            label={downloadQrButtonLabel}
            onClick={downloadQRCode}
            type="secondary"
            disabled={!shareLink}
            className="w-full sm:w-auto"
          />
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 p-4 shadow-sm">
        <StepHeader
          step={4}
          title={emailStepTitle}
          description={emailStepDescription}
        />
        <div className="mt-4">
          {emailAlert && (
            <div className="mb-3">
              <Alert message={emailAlert.message} level={emailAlert.level} />
            </div>
          )}
          <div className="flex flex-wrap gap-2 mb-4">
            {emails.map((email) => (
              <EmailChip
                key={email}
                email={email}
                onDelete={() => handleRemoveEmail(email)}
              />
            ))}
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <input
              type="text"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={handleAddEmail}
              onBlur={() => {
                addEmail();
              }}
              placeholder={t("emailPlaceholder")}
              className="w-full border border-gray-300 p-2 rounded"
            />
            <button
              type="button"
              onClick={handleAddEmailClick}
              aria-label={addEmailButtonAria}
              className="flex w-full items-center justify-center rounded-lg border border-primary text-primary p-2 text-xl font-semibold sm:w-12 sm:h-12 sm:p-0"
            >
              ✓
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-2">{addEmailHint}</p>
          <LoadingButton
            label={
              isSending
                ? t("sendingEmail")
                : `${t("sendEmail")} (${emails.length})`
            }
            onClick={handleSendEmail}
            progress={progress}
            disabled={isSending}
            className="mt-4 w-full sm:w-auto"
          />
        </div>
      </section>
    </div>
  );
};

export default withTranslations(LOCALE)(ShareTabContent);
